import React, { useEffect, useState, useMemo } from "react";
import {
  Trash2,
  Edit,
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  PlusCircle,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Apiurl from "../../environment/Apiurl";

function SupplierPharmacy() {
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showEntries, setShowEntries] = useState("10");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [generateSupplierId, setGenerateSupplierId] = useState("");

  // Fetch generated supplier ID
  const fetchGenerate = async () => {
    try {
      console.log(
        "Fetching generated supplier ID from:",
        `${Apiurl}/generate_supplier_id`
      );
      const response = await axios.get(`${Apiurl}/generate_supplier_id`);
      console.log("Generated supplier ID:", response.data.supplier_id);
      setGenerateSupplierId(response.data.supplier_id || "");
    } catch (error) {
      console.error(
        "Error fetching generated supplier_id:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to generate supplier ID.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Form state for adding
  const [addData, setAddData] = useState({
    supplier_id: "",
    supplier_name: "",
    contact_person: "",
    mobile: "",
    email: "",
    gstin_no: "",
    address: "",
    city: "",
    state: "",
    pin_code: "",
    bank_name: "",
    bank_account_no: "",
    ifsc_code: "",
    notes: "",
    status: "",
    created_by: localStorage.getItem("user_id") || "unknown",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    supplier_id: "",
    supplier_name: "",
    contact_person: "",
    mobile: "",
    email: "",
    gstin_no: "",
    address: "",
    city: "",
    state: "",
    pin_code: "",
    bank_name: "",
    bank_account_no: "",
    ifsc_code: "",
    notes: "",
    status: "",
    updated_by: localStorage.getItem("user_id") || "unknown",
  });

  // Fetch user privileges based on role_name
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRole = localStorage.getItem("role_name") || "unknown";
      console.log("Fetching privileges for role_name:", userRole);

      if (userRole === "unknown") {
        console.warn("No role_name found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log(
        "Fetched roles data:",
        JSON.stringify(response.data, null, 2)
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          `Invalid or empty roles data received: ${JSON.stringify(
            response.data
          )}`
        );
      }

      const roleData = response.data.find(
        (role) => role.role_name === userRole
      );

      if (roleData) {
        console.log(
          "Found role data for role_name:",
          userRole,
          JSON.stringify(roleData, null, 2)
        );
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
          console.log(
            `Processing page: ${pageName}, Access: ${page.access}, Privileges:`,
            JSON.stringify(pagePrivileges, null, 2)
          );
        });
        console.log(
          "Set user privileges:",
          JSON.stringify(privileges, null, 2)
        );
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_name:", userRole);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Supplier page
  const hasPermission = (permission) => {
    const supplierPrivileges = userPrivileges["Supplier"];
    if (!supplierPrivileges) {
      console.log("No privileges found for Supplier page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Supplier:`,
      JSON.stringify(supplierPrivileges, null, 2)
    );
    if (permission === "access") {
      const accessGranted = supplierPrivileges.access === true;
      console.log(`Access permission result: ${accessGranted}`);
      return accessGranted;
    }
    const hasPerm =
      supplierPrivileges.all === true ||
      supplierPrivileges[permission] === true;
    console.log(`Permission '${permission}' result: ${hasPerm}`);
    return hasPerm;
  };

  // Fetch supplier data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Supplier page");
      setError("You do not have permission to view supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log(
        "Fetching supplier data from:",
        `${Apiurl}/supplier_pharmacy`
      );
      const response = await axios.get(`${Apiurl}/supplier_pharmacy`);
      console.log(
        "Fetched supplier data:",
        JSON.stringify(response.data, null, 2)
      );
      setResponseData(response.data.reverse() || []);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching supplier data:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch supplier data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges and supplier ID...");
    fetchUserPrivileges();
    fetchGenerate();
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Supplier page");
      if (hasPermission("read")) {
        console.log("Fetching supplier data due to read permission");
        fetchData();
      } else {
        console.warn("Read permission denied for Supplier page");
        setError("You do not have permission to view supplier records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Supplier page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Update addData.supplier_id when generateSupplierId changes
  useEffect(() => {
    setAddData((prev) => ({
      ...prev,
      supplier_id: generateSupplierId,
    }));
  }, [generateSupplierId]);

    useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.supplier_id,
        item.supplier_name,
        item.contact_person,
        item.mobile,
        item.email,
        item.status,
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );
    console.log("Filtered supplier data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  // Validate form data
  const validateFormData = (data) => {
    const requiredFields = [
      "supplier_id",
      "supplier_name",
      "contact_person",
      "mobile",
      "email",
      "gstin_no",
      "address",
      "city",
      "state",
      "pin_code",
      "bank_name",
      "bank_account_no",
      "ifsc_code",
      "status",
    ];
    for (const field of requiredFields) {
      // if (!data[field] || data[field].trim() === "") {
      //   return `Please fill in the ${field.replace("_", " ")} field.`;
      // }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return "Please enter a valid email address.";
    }
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(data.mobile)) {
      return "Please enter a valid 10-digit mobile number.";
    }
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(data.pin_code)) {
      return "Please enter a valid 6-digit pin code.";
    }
    // const gstinRegex =
    //   /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    // if (!gstinRegex.test(data.gstin_no)) {
    //   return "Please enter a valid GSTIN number.";
    // }
    // const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    // if (!ifscRegex.test(data.ifsc_code)) {
    //   return "Please enter a valid IFSC code.";
    // }
    return null;
  };

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Supplier page");
      setError("You do not have permission to add supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const formData = { ...addData, supplier_id: generateSupplierId };
    const validationError = validateFormData(formData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingAdd(true);
    try {
      console.log(
        "Adding supplier with data:",
        JSON.stringify(formData, null, 2)
      );
      await axios.post(`${Apiurl}/supplier_pharmacy`, formData);
      await fetchData();
      await fetchGenerate();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding supplier:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to add supplier. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingAdd(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Supplier page");
      setError("You do not have permission to update supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingId) {
      console.warn("Invalid supplier record ID:", editingId);
      setError("Invalid supplier record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateFormData(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingEdit(true);
    try {
      console.log(
        "Updating supplier with ID:",
        editingId,
        "data:",
        JSON.stringify(updateData, null, 2)
      );
      await axios.put(`${Apiurl}/supplier_pharmacy/${editingId}`, updateData);
      await fetchData();
      closeEditModal();
    } catch (error) {
      console.error(
        "Error updating supplier:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to update supplier. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingEdit(false);
    }
  };

  // Reset add form
  const resetAddForm = () => {
    setAddData({
      supplier_id: generateSupplierId,
      supplier_name: "",
      contact_person: "",
      mobile: "",
      email: "",
      gstin_no: "",
      address: "",
      city: "",
      state: "",
      pin_code: "",
      bank_name: "",
      bank_account_no: "",
      ifsc_code: "",
      notes: "",
      status: "",
      created_by: localStorage.getItem("user_id") || "unknown",
    });
  };

  // Edit supplier
  const handleEdit = (id, supplier) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Supplier page");
      setError("You do not have permission to update supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid supplier record ID:", id);
      setError("Invalid supplier record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Edit Supplier modal for ID:", id, "data:", supplier);
    setEditingId(id);
    setUpdateData({
      supplier_id: supplier.supplier_id || "",
      supplier_name: supplier.supplier_name || "",
      contact_person: supplier.contact_person || "",
      mobile: supplier.mobile || "",
      email: supplier.email || "",
      gstin_no: supplier.gstin_no || "",
      address: supplier.address || "",
      city: supplier.city || "",
      state: supplier.state || "",
      pin_code: supplier.pin_code || "",
      bank_name: supplier.bank_name || "",
      bank_account_no: supplier.bank_account_no || "",
      ifsc_code: supplier.ifsc_code || "",
      notes: supplier.notes || "",
      status: supplier.status || "",
      updated_by: localStorage.getItem("user_id") || "unknown",
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Supplier page");
      setError("You do not have permission to delete supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid supplier record ID:", id);
      setError("Invalid supplier record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Supplier modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Supplier modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete supplier
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Supplier page");
      setError("You do not have permission to delete supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid supplier record ID:", deleteId);
      setError("Invalid supplier record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingDelete(true);
    try {
      console.log("Deleting supplier with ID:", deleteId);
      await axios.delete(`${Apiurl}/supplier_pharmacy/${deleteId}`);
      await fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting supplier:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to delete supplier. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingDelete(false);
    }
  };

  // Preview supplier with navigation
  const handlePreview = (supplier) => {
    if (!hasPermission("download")) {
      console.warn("Download permission denied for Supplier page");
      setError("You do not have permission to view supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!supplier._id) {
      console.warn("Invalid supplier record ID:", supplier._id);
      setError("Invalid supplier record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to View Supplier page for ID:",
      supplier._id,
      "data:",
      supplier
    );
    // toggleSidebar();
    navigate(`/pharmacy/supplier-pharmacy/view-supplier/${supplier._id}`, {
      state: { supplier },
    });
  };

  // Modal handlers
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Supplier page");
      setError("You do not have permission to add supplier records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Supplier modal");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    console.log("Closing Add Supplier modal");
    setShowAddModal(false);
    resetAddForm();
    setError(null);
  };

  const closeEditModal = () => {
    console.log("Closing Edit Supplier modal");
    setShowEditModal(false);
    setEditingId(null);
    setError(null);
  };

  const closePreviewModal = () => {
    console.log("Closing Preview Supplier modal");
    setShowPreviewModal(false);
    setSelectedUser(null);
    setError(null);
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_name:",
      localStorage.getItem("role_name"),
      "on Supplier page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button className="btn btn-custom btn-sm" onClick={openAddModal}>
            <PlusCircle size={16} className="me-2" /> Add Supplier
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by ID, name, contact, mobile, or email"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <XCircle size={16} className="text-muted" />
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Supplier ID</th>
                  <th>Supplier Name</th>
                  <th>Contact Person</th>
                  <th>Phone Number</th>
                  <th>Status</th>
                  {(hasPermission("download") ||
                    hasPermission("update") ||
                    hasPermission("delete")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{item.supplier_id || "N/A"}</td>
                      <td>{item.supplier_name || "N/A"}</td>
                      <td>{item.contact_person || "N/A"}</td>
                      <td>{item.mobile || "N/A"}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center ${
                            item.status?.toLowerCase() === "active"
                              ? "bg-success text-white"
                              : "bg-danger text-white"
                          }`}
                        >
                          {item.status?.toLowerCase() === "active" ? (
                            <CheckCircle size={16} className="me-2" />
                          ) : (
                            <XCircle size={16} className="me-2" />
                          )}
                          {item.status?.toLowerCase() === "active"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>
                      {(hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")) && (
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(item._id)}
                              disabled={!item._id}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleEdit(item._id, item)}
                              disabled={!item._id}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")
                          ? 7
                          : 6
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <Search size={24} className="mb-2" />
                          <br />
                          No supplier records found matching your search
                          criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {filteredData.length > 0 && showEntries !== "All" && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage > 1 && handlePageClick(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === currentPage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === currentPage
                            ? { backgroundColor: "#01AEBE", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage < totalPages &&
                      handlePageClick(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addSupplierModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="addSupplierModalLabel"
                >
                  Add Supplier
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="supplier_id"
                          className="form-label fw-bold"
                        >
                          Supplier ID
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="supplier_id"
                          name="supplier_id"
                          placeholder="Enter Supplier ID"
                          value={generateSupplierId}
                          disabled
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="supplier_name"
                          className="form-label fw-bold"
                        >
                          Supplier Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="supplier_name"
                          name="supplier_name"
                          placeholder="Enter Supplier Name"
                          value={addData.supplier_name}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="contact_person"
                          className="form-label fw-bold"
                        >
                          Contact Person
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="contact_person"
                          name="contact_person"
                          placeholder="Enter Contact Person"
                          value={addData.contact_person}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="mobile" className="form-label fw-bold">
                          Phone Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="mobile"
                          name="mobile"
                          placeholder="Enter Phone Number"
                          value={addData.mobile}
                          onChange={handleAddInputChange}
                          maxLength="10"
                          pattern="\d{10}"
                          title="Please enter exactly 10 digits"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-bold">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          placeholder="Enter Email"
                          value={addData.email}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="gstin_no"
                          className="form-label fw-bold"
                        >
                          GSTIN No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="gstin_no"
                          name="gstin_no"
                          placeholder="Enter GSTIN No"
                          value={addData.gstin_no}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="address" className="form-label fw-bold">
                          Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="address"
                          name="address"
                          placeholder="Enter Address"
                          value={addData.address}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="city" className="form-label fw-bold">
                          City
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="city"
                          name="city"
                          placeholder="Enter City"
                          value={addData.city}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="state" className="form-label fw-bold">
                          State
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="state"
                          name="state"
                          placeholder="Enter State"
                          value={addData.state}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="pin_code"
                          className="form-label fw-bold"
                        >
                          Pincode
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="pin_code"
                          name="pin_code"
                          placeholder="Enter Pincode"
                          value={addData.pin_code}
                          onChange={handleAddInputChange}
                          maxLength="6"
                          pattern="\d{6}"
                          title="Please enter exactly 6 digits"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="bank_name"
                          className="form-label fw-bold"
                        >
                          Bank Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bank_name"
                          name="bank_name"
                          placeholder="Enter Bank Name"
                          value={addData.bank_name}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="bank_account_no"
                          className="form-label fw-bold"
                        >
                          Bank Account Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bank_account_no"
                          name="bank_account_no"
                          placeholder="Enter Bank Account Number"
                          value={addData.bank_account_no}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="ifsc_code"
                          className="form-label fw-bold"
                        >
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="ifsc_code"
                          name="ifsc_code"
                          placeholder="Enter IFSC Code"
                          value={addData.ifsc_code}
                          onChange={handleAddInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label fw-bold">
                          Status
                        </label>
                        <select
                          className="form-control"
                          id="status"
                          name="status"
                          value={addData.status}
                          onChange={handleAddInputChange}
                          required
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="notes" className="form-label fw-bold">
                          Notes
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notes"
                          name="notes"
                          placeholder="Enter Notes"
                          value={addData.notes}
                          onChange={handleAddInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loadingAdd}
                    >
                      {loadingAdd ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editSupplierModalLabel"
          aria-hidden={!showEditModal}
        >
          <div
            className="modal-dialog modal-lg  overflow-hidden modal-dialog-centered"
     
          >
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="editSupplierModalLabel"
                >
                  Edit Supplier
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="supplier_id"
                          className="form-label fw-bold"
                        >
                          Supplier ID
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="supplier_id"
                          name="supplier_id"
                          placeholder="Enter Supplier ID"
                          value={updateData.supplier_id}
                          disabled
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="supplier_name"
                          className="form-label fw-bold"
                        >
                          Supplier Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="supplier_name"
                          name="supplier_name"
                          placeholder="Enter Supplier Name"
                          value={updateData.supplier_name}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="contact_person"
                          className="form-label fw-bold"
                        >
                          Contact Person
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="contact_person"
                          name="contact_person"
                          placeholder="Enter Contact Person"
                          value={updateData.contact_person}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="mobile" className="form-label fw-bold">
                          Phone Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="mobile"
                          name="mobile"
                          placeholder="Enter Phone Number"
                          value={updateData.mobile}
                          onChange={handleEditInputChange}
                          maxLength="10"
                          pattern="\d{10}"
                          title="Please enter exactly 10 digits"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-bold">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          placeholder="Enter Email"
                          value={updateData.email}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="gstin_no"
                          className="form-label fw-bold"
                        >
                          GSTIN No
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="gstin_no"
                          name="gstin_no"
                          placeholder="Enter GSTIN No"
                          value={updateData.gstin_no}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="address" className="form-label fw-bold">
                          Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="address"
                          name="address"
                          placeholder="Enter Address"
                          value={updateData.address}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="city" className="form-label fw-bold">
                          City
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="city"
                          name="city"
                          placeholder="Enter City"
                          value={updateData.city}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="state" className="form-label fw-bold">
                          State
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="state"
                          name="state"
                          placeholder="Enter State"
                          value={updateData.state}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="pin_code"
                          className="form-label fw-bold"
                        >
                          Pincode
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="pin_code"
                          name="pin_code"
                          placeholder="Enter Pincode"
                          value={updateData.pin_code}
                          onChange={handleEditInputChange}
                          maxLength="6"
                          pattern="\d{6}"
                          title="Please enter exactly 6 digits"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="bank_name"
                          className="form-label fw-bold"
                        >
                          Bank Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bank_name"
                          name="bank_name"
                          placeholder="Enter Bank Name"
                          value={updateData.bank_name}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="bank_account_no"
                          className="form-label fw-bold"
                        >
                          Bank Account Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="bank_account_no"
                          name="bank_account_no"
                          placeholder="Enter Bank Account Number"
                          value={updateData.bank_account_no}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label
                          htmlFor="ifsc_code"
                          className="form-label fw-bold"
                        >
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="ifsc_code"
                          name="ifsc_code"
                          placeholder="Enter IFSC Code"
                          value={updateData.ifsc_code}
                          onChange={handleEditInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label fw-bold">
                          Status
                        </label>
                        <select
                          className="form-control"
                          id="status"
                          name="status"
                          value={updateData.status}
                          onChange={handleEditInputChange}
                          required
                        >
                          <option value="">Select Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label htmlFor="notes" className="form-label fw-bold">
                          Notes
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="notes"
                          name="notes"
                          placeholder="Enter Notes"
                          value={updateData.notes}
                          onChange={handleEditInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loadingEdit}
                    >
                      {loadingEdit ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteSupplierModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deleteSupplierModalLabel">
                  Delete Supplier
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this supplier record?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={confirmDelete}
                  disabled={loadingDelete}
                >
                  {loadingDelete ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("download") && (
        <div
          className={`modal fade ${showPreviewModal ? "show" : ""}`}
          style={{ display: showPreviewModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="previewSupplierModalLabel"
          aria-hidden={!showPreviewModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="previewSupplierModalLabel"
                >
                  Supplier Details
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closePreviewModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {selectedUser && (
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Supplier ID
                        </label>
                        <p>{selectedUser.supplier_id || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Supplier Name
                        </label>
                        <p>{selectedUser.supplier_name || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Contact Person
                        </label>
                        <p>{selectedUser.contact_person || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Phone Number
                        </label>
                        <p>{selectedUser.mobile || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Email</label>
                        <p>{selectedUser.email || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">GSTIN No</label>
                        <p>{selectedUser.gstin_no || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Address</label>
                        <p>{selectedUser.address || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">City</label>
                        <p>{selectedUser.city || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">State</label>
                        <p>{selectedUser.state || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Pincode</label>
                        <p>{selectedUser.pin_code || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Bank Name</label>
                        <p>{selectedUser.bank_name || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Bank Account Number
                        </label>
                        <p>{selectedUser.bank_account_no || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">IFSC Code</label>
                        <p>{selectedUser.ifsc_code || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Status</label>
                        <p>{selectedUser.status || "N/A"}</p>
                      </div>
                    </div>
                    <div className="col-lg-8">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Notes</label>
                        <p>{selectedUser.notes || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={closePreviewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal && hasPermission("create")) ||
      (showEditModal && hasPermission("update")) ||
      (showDeleteModal && hasPermission("delete")) ||
      (showPreviewModal && hasPermission("download")) ? (
        <div className="modal-backdrop fade show"></div>
      ) : null}
    </div>
  );
}

export default SupplierPharmacy;
